import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { ShopifyProduct } from '@/lib/shopify';

export interface WishlistItem {
  product: ShopifyProduct;
  variantId: string;
  variantTitle: string;
  price: {
    amount: string;
    currencyCode: string;
  };
  compareAtPrice?: {
    amount: string;
    currencyCode: string;
  } | null;
  selectedOptions: Array<{
    name: string;
    value: string;
  }>;
  sku?: string;
  variantImage?: {
    url: string;
    altText?: string | null;
  };
  quantity: number;
  addedAt: string;
}

interface WishlistStore {
  items: WishlistItem[];
  isOpen: boolean;
  
  // Actions
  addItem: (item: Omit<WishlistItem, 'addedAt' | 'quantity'>) => void;
  removeItem: (variantId: string) => void;
  updateQuantity: (variantId: string, quantity: number) => void;
  clearWishlist: () => void;
  setOpen: (open: boolean) => void;
  isInWishlist: (variantId: string) => boolean;
  getTotalItems: () => number;
}

export const useWishlistStore = create<WishlistStore>()(
  persist(
    (set, get) => ({
      items: [],
      isOpen: false,

      addItem: (item) => {
        const { items } = get();
        const existingItem = items.find(i => i.variantId === item.variantId);
        
        if (!existingItem) {
          set({ 
            items: [...items, { ...item, quantity: 1, addedAt: new Date().toISOString() }] 
          });
        }
      },

      removeItem: (variantId) => {
        set({
          items: get().items.filter(item => item.variantId !== variantId)
        });
      },

      updateQuantity: (variantId, quantity) => {
        if (quantity < 1) return;
        set({
          items: get().items.map(item => 
            item.variantId === variantId ? { ...item, quantity } : item
          ),
        });
      },

      clearWishlist: () => {
        set({ items: [] });
      },

      setOpen: (isOpen) => set({ isOpen }),

      isInWishlist: (variantId) => {
        return get().items.some(item => item.variantId === variantId);
      },

      getTotalItems: () => {
        return get().items.length;
      },
    }),
    {
      name: 'wishlist',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        items: state.items,
      }),
    }
  )
);
